'use client';

import type { SearchableGroupedMultiSelectAnswer } from '@repo/contracts/cardfinder/funnel/types';
import { css } from '@repo/pandacss/css';
import { visuallyHidden } from '@repo/pandacss/patterns';
import { Typography } from '@repo/ui/Typography';
import { useId, useState } from 'react';
import type { HydratedSearchableGroupedMultiSelectStep } from '@/features/funnel/config/types';
import { useAnswer } from '@/features/funnel/state/answer/hooks';
import { FUNNEL_STEP_TITLE_ID } from '../../Wrapper/StepTitle';
import { choiceListItemClass, getChoiceSetPositionProps } from '../choiceGroupA11y';
import { filterGroups } from './filterGroups';
import { OptionRow } from './OptionRow';
import { Search } from './Search';
import { useRovingChoiceFocus } from './useRovingChoiceFocus';
type SearchableGroupedMultiSelectInputProps = {
  step: HydratedSearchableGroupedMultiSelectStep;
};
export function SearchableGroupedMultiSelectInput({
  step
}: SearchableGroupedMultiSelectInputProps) {
  const {
    value,
    setAnswer
  } = useAnswer<SearchableGroupedMultiSelectAnswer>(step.id);
  const arr = value ?? [];
  const [query, setQuery] = useState('');
  const inputId = useId();
  const hintId = useId();
  const statusId = useId();
  const filtered = filterGroups(step.asyncOptions, query);
  const max = step.maxSelections;
  const reachedMax = max !== undefined && arr.length >= max;
  const flatOptions = filtered.flatMap(group => group.options);
  const {
    getTabIndex,
    setItemRef,
    handleFocus,
    handleKeyDown
  } = useRovingChoiceFocus({
    itemCount: flatOptions.length,
    resetKey: query,
    isItemFocusable: index => {
      const option = flatOptions[index];
      if (!option) return false;
      return arr.includes(option.value) || !reachedMax;
    }
  });
  let optionIndex = 0;
  const mobileColumns = step.columns?.mobile ?? 2;
  const tabletDesktopColumns = step.columns?.tabletDesktop ?? 3;
  const isSearching = query.trim().length > 0;
  const toggle = (v: string) => {
    if (arr.includes(v)) {
      setAnswer(arr.filter(x => x !== v));
    } else if (!reachedMax) {
      setAnswer([...arr, v]);
      if (isSearching) {
        setQuery('');
      }
    }
  };
  const selectionStatus = max !== undefined ? `${arr.length} of ${max} selected` : `${arr.length} selected`;
  const searchStatus = isSearching ? `${filtered.length} ${filtered.length === 1 ? 'result' : 'results'} for "${query}"` : '';
  const describedBy = [max !== undefined ? hintId : null, statusId].filter(Boolean).join(' ') || undefined;
  return <fieldset aria-labelledby={FUNNEL_STEP_TITLE_ID} aria-describedby={describedBy} className={css({
    flex: '1',
    minHeight: '[0]',
    minWidth: '[0]',
    width: '[100%]',
    height: '[100%]',
    border: 'none',
    padding: '0',
    margin: '0',
    display: 'flex',
    flexDirection: 'column'
  })}>
      <div className={css({
      display: 'flex',
      flexDirection: 'column',
      flex: '1',
      minHeight: '[0]',
      gap: '3'
    })}>
        {max !== undefined && <Typography as="p" id={hintId} variant="body" css={{
        color: 'neutral.darker',
        fontWeight: 'medium'
      }}>
            Select up to {max}
          </Typography>}
        <p role="status" aria-live="polite" id={statusId} className={visuallyHidden()}>
          {[selectionStatus, searchStatus].filter(Boolean).join('. ')}
        </p>
        <Search id={inputId} value={query} onChange={setQuery} placeholder="Search" ariaLabel="Search options" />
        <div className={css({
        display: 'flex',
        flexDirection: 'column',
        flex: '1',
        minHeight: '[0]',
        gap: '[0.75rem]',
        overflowY: 'auto'
      })}>
          {filtered.length === 0 && <Typography as="p" variant="body" css={{
          py: '4',
          color: 'neutral.darker',
          textAlign: 'center'
        }}>
              No matches for &ldquo;{query}&rdquo;
            </Typography>}
          {filtered.map(group_0 => <fieldset key={group_0.label} className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: '[0.75rem]',
          border: 'none',
          padding: '0',
          margin: '0'
        })}>
              <legend className={isSearching ? visuallyHidden() : css({
            color: 'neutral.darker-2',
            fontWeight: 'semibold',
            fontSize: '[14px]',
            my: '2',
            padding: '0'
          })}>
                {group_0.label}
              </legend>
              <ul className={css({
            display: 'grid',
            gridTemplateColumns: {
              base: '[repeat(var(--searchable-multi-select-grid-cols-mobile), minmax(0, 1fr))]',
              sm: '[repeat(var(--searchable-multi-select-grid-cols-tablet), minmax(0, 1fr))]'
            },
            gap: '[0.75rem]',
            listStyle: 'none',
            padding: '0',
            margin: '0',
            ...(isSearching && {
              display: 'flex',
              flexDirection: 'column'
            })
          })} style={{
            '--searchable-multi-select-grid-cols-mobile': mobileColumns,
            '--searchable-multi-select-grid-cols-tablet': tabletDesktopColumns
          } as React.CSSProperties}>
                {group_0.options.map((option_0, index_0) => {
              const checked = arr.includes(option_0.value);
              const disabled = !checked && reachedMax;
              const globalIndex = optionIndex++;
              return <li key={option_0.value} className={choiceListItemClass}>
                      <OptionRow option={option_0} checked={checked} disabled={disabled} onToggle={toggle} name={step.id} iconVariant={step.chipVariant} setPositionProps={getChoiceSetPositionProps(index_0, group_0.options.length)} tabIndex={getTabIndex(globalIndex)} inputRef={setItemRef(globalIndex)} onInputFocus={handleFocus(globalIndex)} onInputKeyDown={handleKeyDown(globalIndex)} />
                    </li>;
            })}
              </ul>
            </fieldset>)}
        </div>
      </div>
    </fieldset>;
}